import { useCallback, useEffect, useRef, useState, type MutableRefObject } from 'react';
import { renderAsync } from 'docx-preview';
import {
  // Crosshair,
  Eye,
  Info,
  Loader,
  Maximize,
  MousePointerClick,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';
import { Button } from '@/components/ui-kit/button';
import { useToast } from '@/hooks/use-toast';
import { occurrenceAt } from '@/lib/docx/text-locate';
import {
  applyFloatingImageFixups,
  ensureWordFontFaces,
  parseDocxAnchors,
} from '@/lib/docx/render-fidelity';
import {
  bodySpanOfRange,
  buildBodyModel,
  isExcludedFromBody,
  locateInBody,
  occurrenceOf,
  rangeFromBodyOffsets,
} from './body-model';
import { HighlightOverlay } from './HighlightOverlay';
import { useDocRootRef, useDocxSelection } from './context';
import type { Rect } from './types';

const MIN_ZOOM = 0.5;
const MAX_ZOOM = 2;

/** Chars of surrounding text stored with a selection to tell repeated phrases apart. */
const CONTEXT_CHARS = 40;

export function DocxCanvas() {
  const { toast } = useToast();
  const {
    docxBuffer,
    loadError,
    variables,
    fieldErrors,
    activeHighlight,
    openDraftFor,
    registerScrollToField,
  } = useDocxSelection();

  const docRef = useDocRootRef() as MutableRefObject<HTMLDivElement | null>;
  const scrollRef = useRef<HTMLDivElement>(null);
  const scaleWrapRef = useRef<HTMLDivElement>(null);

  const [rendering, setRendering] = useState(true);
  const [renderError, setRenderError] = useState<string | null>(null);
  const [naturalSize, setNaturalSize] = useState<{ w: number; h: number } | null>(null);
  const [fitScale, setFitScale] = useState(1);
  const [userZoom, setUserZoom] = useState(1);
  const [highlights, setHighlights] = useState<Record<string, Rect[]>>({});
  const [layoutVersion, setLayoutVersion] = useState(0);
  const [showFields, setShowFields] = useState(false);

  const appliedScale = fitScale * userZoom;

  const measureAndStore = useCallback(() => {
    const el = docRef.current;
    if (!el) return;
    const wrapper = el.firstElementChild as HTMLElement | null;
    const w = wrapper?.scrollWidth || el.scrollWidth || 0;
    const h = wrapper?.scrollHeight || el.scrollHeight || 0;
    if (w <= 0 || h <= 0) return;
    setNaturalSize((prev) =>
      prev && Math.abs(prev.w - w) <= 1 && Math.abs(prev.h - h) <= 1 ? prev : { w, h }
    );
  }, [docRef]);

  // Render the document with docx-preview once the bytes are available.
  useEffect(() => {
    const el = docRef.current;
    if (!el || !docxBuffer) return;
    let cancelled = false;
    el.innerHTML = '';
    setRendering(true);
    setRenderError(null);
    setNaturalSize(null);
    setHighlights({});

    const onFontsDone = () => {
      if (!cancelled) {
        measureAndStore();
        setLayoutVersion((v) => v + 1);
      }
    };

    (async () => {
      try {
        ensureWordFontFaces();
        const anchors = parseDocxAnchors(docxBuffer);
        await renderAsync(new Uint8Array(docxBuffer), el, undefined, {
          className: 'docxpv',
          inWrapper: true,
          ignoreWidth: false,
          ignoreHeight: false,
          breakPages: true,
          // Honor Word's cached page-break hints so the marking view paginates the same way
          // the generated preview does (docx-preview ignores them by default and would lay
          // the whole document onto one tall page).
          ignoreLastRenderedPageBreak: false,
          renderHeaders: true,
          renderFooters: true,
          renderFootnotes: true,
          renderEndnotes: true,
          useBase64URL: true,
          experimental: true,
        });
        if (cancelled) return;
        try {
          await document.fonts.ready;
        } catch {
          /* fonts API unavailable — measure anyway */
        }
        await Promise.all(
          Array.from(el.querySelectorAll('img')).map((img) =>
            (img as HTMLImageElement).decode().catch(() => undefined)
          )
        );
        if (cancelled) return;
        applyFloatingImageFixups(el, anchors);
        measureAndStore();
      } catch {
        if (!cancelled) setRenderError('Could not render the document preview.');
      } finally {
        if (!cancelled) setRendering(false);
      }
    })();

    document.fonts?.addEventListener?.('loadingdone', onFontsDone);
    return () => {
      cancelled = true;
      document.fonts?.removeEventListener?.('loadingdone', onFontsDone);
    };
  }, [docxBuffer, measureAndStore, docRef]);

  // Fit-to-width: recompute the auto scale on container resize.
  useEffect(() => {
    const container = scrollRef.current;
    if (!container || !naturalSize || naturalSize.w === 0) return;
    const compute = () => {
      const avail = container.clientWidth - 24;
      setFitScale(Math.min(1, avail / naturalSize.w));
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(container);
    return () => ro.disconnect();
  }, [naturalSize]);

  // Re-measure if the rendered content's own box changes (late fonts, reflow).
  useEffect(() => {
    if (rendering) return;
    const wrapper = docRef.current?.firstElementChild as HTMLElement | null;
    if (!wrapper) return;
    let raf = 0;
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        measureAndStore();
        setLayoutVersion((v) => v + 1);
      });
    });
    ro.observe(wrapper);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [measureAndStore, rendering, docRef]);

  // Compute highlight rectangles for every marked field (unscaled coords).
  const recomputeHighlights = useCallback(() => {
    const wrap = scaleWrapRef.current;
    const target = docRef.current;
    if (!wrap || !target || !target.firstElementChild) {
      setHighlights({});
      return;
    }
    const model = buildBodyModel(target);
    const wrapBox = wrap.getBoundingClientRect();
    const s = appliedScale || 1;
    const next: Record<string, Rect[]> = {};
    for (const v of variables) {
      const loc = locateInBody(
        model,
        v.selectedText,
        v.contextBefore,
        v.contextAfter,
        occurrenceOf(v)
      );
      if (!loc) continue;
      const range = rangeFromBodyOffsets(model, loc.rawStart, loc.rawEnd);
      if (!range) continue;
      const rects = Array.from(range.getClientRects())
        .map((r) => ({
          x: (r.left - wrapBox.left) / s,
          y: (r.top - wrapBox.top) / s,
          w: r.width / s,
          h: r.height / s,
        }))
        .filter((r) => r.w > 0.5 && r.h > 0.5);
      if (rects.length) next[v.name] = rects;
    }
    setHighlights(next);
  }, [variables, appliedScale, docRef]);

  useEffect(() => {
    if (rendering || renderError) return;
    const raf = requestAnimationFrame(recomputeHighlights);
    return () => cancelAnimationFrame(raf);
  }, [recomputeHighlights, rendering, renderError, naturalSize, layoutVersion]);

  // Reset the preview toggle when no fields remain.
  useEffect(() => {
    if (variables.length === 0 && showFields) setShowFields(false);
  }, [variables.length, showFields]);

  const captureSelection = useCallback(() => {
    const sel = window.getSelection();
    const target = docRef.current;
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed || !target) return;

    const range = sel.getRangeAt(0);
    if (!target.contains(range.startContainer) || !target.contains(range.endContainer)) return;
    if (
      isExcludedFromBody(range.startContainer, target) ||
      isExcludedFromBody(range.endContainer, target)
    ) {
      toast({
        variant: 'destructive',
        title:
          "That text is in a header, footer or footnote — those can't be turned into fields yet.",
      });
      return;
    }

    const raw = sel.toString();
    const text = raw.replace(/\s+/g, ' ').trim();
    if (text.length < 1) {
      toast({ title: 'Select a bit more text to mark it as a field.' });
      return;
    }

    const model = buildBodyModel(target);
    // Both ends come from the range's intersection with the model — see bodySpanOfRange for
    // why `startOffset + sel.toString().length` cannot be used for the end.
    const span = bodySpanOfRange(model, range);
    const startOffset = span ? span.start : model.text.indexOf(raw);
    const endOffset = span ? span.end : startOffset >= 0 ? startOffset + raw.length : -1;
    const contextBefore =
      startOffset >= 0 ? model.text.slice(Math.max(0, startOffset - CONTEXT_CHARS), startOffset) : '';
    const contextAfter =
      endOffset >= 0 ? model.text.slice(endOffset, endOffset + CONTEXT_CHARS) : '';

    const p =
      range.startContainer.nodeType === Node.TEXT_NODE
        ? range.startContainer.parentElement?.closest('p')
        : (range.startContainer as Element).closest?.('p');
    let paragraphIndex: number | null = null;
    if (p) {
      const ps = Array.from(target.querySelectorAll('p')).filter(
        (pp) => !isExcludedFromBody(pp, target)
      );
      const i = ps.indexOf(p as HTMLParagraphElement);
      paragraphIndex = i >= 0 ? i : null;
    }

    // Record WHICH of the repeated occurrences was clicked. Once the selection is reduced to
    // text plus context this is unrecoverable, and for text that repeats identically it is
    // the only thing that identifies the span the admin meant.
    const occurrence = startOffset >= 0 ? occurrenceAt(model.text, text, startOffset) : null;

    openDraftFor(text, contextBefore, contextAfter, paragraphIndex, {
      occurrenceIndex: occurrence ? occurrence.index : null,
      occurrenceCount: occurrence ? occurrence.count : null,
    });
  }, [docRef, openDraftFor, toast]);

  // Register the scroll-to-field impl with the provider so the panel can drive it.
  useEffect(() => {
    const impl = (name: string) => {
      const container = scrollRef.current;
      if (!container) return;
      let rect = highlights[name]?.[0];
      if (!rect) {
        const target = docRef.current;
        const wrap = scaleWrapRef.current;
        const v = variables.find((x) => x.name === name);
        if (target && wrap && v) {
          const model = buildBodyModel(target);
          const loc = locateInBody(
            model,
            v.selectedText,
            v.contextBefore,
            v.contextAfter,
            occurrenceOf(v)
          );
          const range = loc && rangeFromBodyOffsets(model, loc.rawStart, loc.rawEnd);
          const r = range?.getClientRects()[0];
          if (r) {
            const wb = wrap.getBoundingClientRect();
            const s = appliedScale || 1;
            rect = {
              x: (r.left - wb.left) / s,
              y: (r.top - wb.top) / s,
              w: r.width / s,
              h: r.height / s,
            };
          }
        }
      }
      if (rect) {
        const top = rect.y * appliedScale + 12;
        container.scrollTo({ top: Math.max(0, top - 80), behavior: 'smooth' });
      }
    };
    registerScrollToField(impl);
    return () => registerScrollToField(null);
  }, [highlights, variables, appliedScale, registerScrollToField, docRef]);

  const zoomBy = (delta: number) =>
    setUserZoom((z) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Math.round((z + delta) * 100) / 100)));

  const spacerHeight = naturalSize ? naturalSize.h * appliedScale : undefined;
  const spacerWidth = naturalSize ? naturalSize.w * appliedScale : undefined;

  return (
    <div className="flex flex-col gap-2 min-w-0 h-full">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-2 shrink-0">
        <div className=" items-center gap-2 text-sm text-globe-grey hidden md:flex">
          <MousePointerClick className="h-4 w-4 shrink-0" />
          <span>Select text in the document, then mark it as a field.</span>
        </div>
        <div className="flex items-center gap-1">
          {/* <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-1.5"
            // Keep the live selection alive: a focusable button collapses it on mousedown.
            onMouseDown={(e) => e.preventDefault()}
            onClick={captureSelection}
          >
            <Crosshair className="h-3.5 w-3.5" />
            Make this a field
          </Button> */}
          {variables.length > 0 && (
            <Button
              type="button"
              variant={showFields ? 'primary' : 'outline'}
              size="sm"
              className="gap-1.5"
              onClick={() => setShowFields((s) => !s)}
              aria-pressed={showFields}
              title="Switch between the original text and your field labels"
            >
              <Eye className="h-3.5 w-3.5" />
              {showFields ? 'Show original' : 'Show fields'}
            </Button>
          )}
          <div className="flex items-center rounded-lg border border-border">
            <button
              type="button"
              onClick={() => zoomBy(-0.1)}
              disabled={userZoom <= MIN_ZOOM}
              className="flex h-8 w-8 items-center justify-center rounded-l-lg text-globe-grey hover:bg-muted disabled:opacity-40"
              aria-label="Zoom out"
            >
              <ZoomOut className="h-4 w-4" />
            </button>
            <span
              className="min-w-12 text-center text-xs tabular-nums text-globe-grey"
              aria-live="polite"
            >
              {Math.round(appliedScale * 100)}%
            </span>
            <button
              type="button"
              onClick={() => zoomBy(0.1)}
              disabled={userZoom >= MAX_ZOOM}
              className="flex h-8 w-8 items-center justify-center text-globe-grey hover:bg-muted disabled:opacity-40"
              aria-label="Zoom in"
            >
              <ZoomIn className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => setUserZoom(1)}
              className="flex h-8 w-8 items-center justify-center rounded-r-lg border-l border-border text-globe-grey hover:bg-muted"
              aria-label="Reset zoom to fit width"
              title="Fit width"
            >
              <Maximize className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Document */}
      <div
        ref={scrollRef}
        tabIndex={0}
        className="relative flex-1 min-h-0 rounded-lg border border-border bg-muted/40 overflow-auto p-3"
        role="region"
        aria-label="Document preview — select text, then choose Make this a field"
        aria-busy={rendering}
      >
        {rendering && (
          <div
            role="status"
            aria-live="polite"
            className="absolute inset-0 z-10 flex items-center justify-center bg-card/70"
          >
            <Loader className="h-6 w-6 animate-spin text-selise-blue" />
            <span className="sr-only">Loading document preview…</span>
          </div>
        )}
        {renderError || loadError ? (
          <div className="p-6 text-sm text-crimson">{renderError || loadError}</div>
        ) : (
          <div style={{ height: spacerHeight, width: spacerWidth }}>
            <div
              ref={scaleWrapRef}
              style={{
                transform: `scale(${appliedScale})`,
                transformOrigin: 'top left',
                width: naturalSize ? naturalSize.w : undefined,
                position: 'relative',
              }}
            >
              <div ref={docRef} onMouseUp={captureSelection} className="select-text cursor-text" />
              <HighlightOverlay
                highlights={highlights}
                variables={variables}
                fieldErrors={fieldErrors}
                activeHighlight={activeHighlight}
                showFields={showFields}
              />
            </div>
          </div>
        )}
      </div>

      <p className="hidden md:flex items-start gap-1.5  text-xs text-globe-grey shrink-0">
        <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
        <span>
          This is your real document, rendered from the file you uploaded. Formatting, images and
          layout are preserved — exact line and page breaks are finalized when a document is
          generated.
        </span>
      </p>
    </div>
  );
}
